import PageTitle from "../Common/PageTitle"
import { FC, useEffect, useState } from "react"
import { Avatar, Box, Button, Chip, CircularProgress, Rating, Typography } from "@mui/material"
import { StarRounded } from "@mui/icons-material"
import { Apis, getImageUrl } from "../../helpers"
import { PropertyProps } from "../../types/properties"
import { Connect, mapStateToProps } from "../../Redux"
import NoData from "../Common/NoData"

const PAGE_SIZE = 10
const STARS = [5, 4, 3, 2, 1]

interface ReviewRow {
    rater: { name: string, image?: any }
    date: string | Date
    review: string
    rating: number
    ratingText: string
}
interface Props {
    selectedProperty?: PropertyProps
}
interface StateProps {
    data: ReviewRow[]
    loading: boolean
    fetchingMore: boolean
    hasMore: boolean
    page: number
    selectedRating: number
}
const Reviews: FC<Props> = ({
    selectedProperty
}) => {
    const [state, setState] = useState<StateProps>({
        data: [],
        loading: true,
        fetchingMore: false,
        hasMore: false,
        page: 0,
        selectedRating: 0
    })
    const { data, loading, fetchingMore, hasMore, page, selectedRating } = state

    const getReviews = ({ resetting, loadingMore, rating }: any) => {
        const newRating = rating !== undefined ? rating : selectedRating
        const Page = resetting ? 0 : loadingMore ? page : 0
        Apis({
            module: 'Rating',
            action: 'getReviews',
            itemId: selectedProperty?._id,
            type: 'property',
            selectedRating: newRating || undefined,
            pageSize: PAGE_SIZE,
            page: Page
        }).then((resp: any) => {
            const respData = resp || []
            setState(s => ({
                ...s,
                data: loadingMore ? [...s.data, ...respData] : respData,
                loading: false,
                fetchingMore: false,
                hasMore: respData.length >= PAGE_SIZE,
                page: Page + 1,
                selectedRating: newRating
            }))
        }).catch(() => {
            setState(s => ({ ...s, loading: false, fetchingMore: false }))
        })
    }

    useEffect(() => {
        getReviews({ resetting: true })
        // eslint-disable-next-line
    }, [])

    const onSelectStar = (star: number) => {
        setState(s => ({ ...s, loading: true, data: [] }))
        getReviews({ resetting: true, rating: selectedRating === star ? 0 : star })
    }

    const loadMore = () => {
        setState(s => ({ ...s, fetchingMore: true }))
        getReviews({ loadingMore: true })
    }

    return (
        <Box sx={{ height: '100%', pr: 5 }}>
            <PageTitle title={"Reviews"} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2, mb: 3 }}>
                {
                    STARS.map(star => (
                        <Chip
                            key={star}
                            clickable
                            onClick={() => onSelectStar(star)}
                            color={selectedRating === star ? 'primary' : 'default'}
                            variant={selectedRating === star ? 'filled' : 'outlined'}
                            icon={<StarRounded sx={{ fontSize: 16 }} />}
                            label={star}
                        />
                    ))
                }
            </Box>
            {
                !data.length || loading ?
                    <NoData
                        message={`No reviews yet.`}
                        loading={loading}
                    /> :
                    <Box sx={{ maxWidth: 700 }}>
                        {
                            data.map((review, i) => (
                                <Box
                                    key={i}
                                    sx={{
                                        display: 'flex',
                                        gap: 2,
                                        py: 2,
                                        borderBottom: i === data.length - 1 ? 'none' : '1px solid rgba(0,0,0,0.08)'
                                    }}
                                >
                                    <Avatar src={getImageUrl(review.rater?.image, 'small')} sx={{ width: 40, height: 40 }} />
                                    <Box sx={{ flex: 1 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography sx={{ fontWeight: 'bold', fontSize: 14 }}>{review.rater?.name}</Typography>
                                            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                                                {review.date ? new Date(review.date).toLocaleDateString('en-KE', { year: 'numeric', month: 'short', day: 'numeric' }) : ''}
                                            </Typography>
                                        </Box>
                                        <Rating value={review.rating} readOnly size="small" />
                                        {
                                            review.ratingText ?
                                                <Typography sx={{ fontSize: 13, fontWeight: 600, mt: 0.5 }}>{review.ratingText}</Typography> :
                                                null
                                        }
                                        {
                                            review.review ?
                                                <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.5 }}>{review.review}</Typography> :
                                                null
                                        }
                                    </Box>
                                </Box>
                            ))
                        }
                        {
                            hasMore ?
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
                                    <Button
                                        variant="outlined"
                                        sx={{ textTransform: 'none' }}
                                        disabled={fetchingMore}
                                        onClick={loadMore}
                                    >
                                        {fetchingMore ? <CircularProgress size={18} /> : 'Load More'}
                                    </Button>
                                </Box> :
                                null
                        }
                    </Box>
            }
        </Box>
    )
}

export default Connect(mapStateToProps)(Reviews)
